import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

export function useSpeech() {
  const audioRef = useRef(null);
  const queueRef = useRef([]);
  const [speaking, setSpeaking] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  const [activeKey, setActiveKey] = useState(null);

  const stop = useCallback(() => {
    queueRef.current = [];
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeaking(false); setActiveKey(null); setLoadingKey(null);
  }, []);

  const playOne = useCallback(async (text, key) => {
    setLoadingKey(key);
    let url;
    try {
      const { data } = await api.post("/tts", { text: text.slice(0, 3800) }, { responseType: "blob" });
      url = URL.createObjectURL(data);
    } catch (e) {
      console.warn("[ARIA] TTS failed", e);
      setLoadingKey(null);
      return false;
    }
    const audio = new Audio(url);
    audioRef.current = audio;
    setLoadingKey(null); setSpeaking(true); setActiveKey(key);
    return new Promise((resolve) => {
      audio.onended = () => { URL.revokeObjectURL(url); resolve(true); };
      audio.onerror = () => { URL.revokeObjectURL(url); resolve(false); };
      audio.play().catch(() => { URL.revokeObjectURL(url); resolve(false); });
    });
  }, []);

  const speak = useCallback(async (text, key = "default") => {
    if (!text) return;
    stop();
    await playOne(text, key);
    setSpeaking(false); setActiveKey(null);
  }, [stop, playOne]);

  const speakSequence = useCallback(async (items) => {
    stop();
    queueRef.current = items.slice();
    while (queueRef.current.length) {
      const [text, key] = queueRef.current.shift();
      const ok = await playOne(text, key);
      if (!ok) break;
    }
    setSpeaking(false); setActiveKey(null);
  }, [stop, playOne]);

  const toggle = useCallback(
    (text, key = "default") => (speaking && activeKey === key ? stop() : speak(text, key)),
    [speaking, activeKey, stop, speak],
  );

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);
  return { speak, speakSequence, stop, toggle, speaking, activeKey, loadingKey };
}
